import { useFeatureGate } from '@/features/billing/gates';
import type { Vibe } from '@/features/colors/vibe.types';
import { VIBES } from '@/features/colors/vibes';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import type { FeatureFlag } from '@/hooks/useFeatureFlag/defaults';
import type { ExperienceVibe } from '@/types/gql.generated';
import { Text } from '@/ui';
import { SelectDropdown, Option } from '../SelectDropdown';

type Props = {
  value: ExperienceVibe;
  onChange: (vibe: ExperienceVibe) => void;
  onPreview: (vibe: ExperienceVibe | null) => void;
};

type OptionProps = { vibe: Vibe; isActive: boolean };

type PickerOptionProps = OptionProps & {
  isLocked?: boolean;
  onLockedClick?: () => void;
};

const PickerOption = ({
  vibe,
  isActive,
  isLocked,
  onLockedClick,
}: PickerOptionProps) => {
  return (
    <Option
      isActive={isActive}
      isLocked={isLocked}
      option={vibe}
      onLockedClick={onLockedClick}
    >
      <Text
        color={isActive ? 'magicPurple.700' : 'ink.strong'}
        flex={1}
        fontFamily={vibe.meta.fontFamily}
        fontSize="ah-control"
        fontWeight={isActive ? 700 : vibe.meta.fontWeight}
      >
        {vibe.meta.name}
      </Text>
    </Option>
  );
};

const FlaggedVibeOption = ({
  featureFlag,
  ...props
}: PickerOptionProps & { featureFlag: FeatureFlag }) => {
  const { value: isEnabled } = useFeatureFlag(featureFlag);

  if (!isEnabled) {
    return null;
  }

  return <PickerOption {...props} />;
};

const VibeOption = ({ vibe, ...props }: PickerOptionProps) => {
  if (vibe.meta.featureFlag) {
    return (
      <FlaggedVibeOption
        featureFlag={vibe.meta.featureFlag}
        vibe={vibe}
        {...props}
      />
    );
  }

  return <PickerOption vibe={vibe} {...props} />;
};

export const VibeSelect = ({ value, onChange, onPreview }: Props) => {
  // Resolved once for the dropdown rather than per option: every option would
  // otherwise repeat the same gate and profile read to reach the same answer.
  const { isGated, isLocked, promptUpgrade } = useFeatureGate('premiumVibe');

  return (
    <SelectDropdown
      options={Object.values(VIBES)}
      value={value}
      renderOption={(vibe, isActive) => {
        const requiresPaidPlan = Boolean(vibe.meta.requiresPaidPlan);

        return (
          <VibeOption
            isActive={isActive}
            // A paid vibe stays in the list and offers the upgrade rather than
            // vanishing — hiding it was the one gate in the app that gave the user
            // nothing to act on. Locked on `isLocked` so it can't be selected
            // before entitlement resolves, but the upgrade is only offered once
            // `isGated` confirms one would help; Option treats a missing handler as
            // a no-op, so mid-load the option is simply inert.
            isLocked={requiresPaidPlan && isLocked}
            vibe={vibe}
            onLockedClick={
              requiresPaidPlan && isGated ? promptUpgrade : undefined
            }
          />
        );
      }}
      renderTrigger={(active) => (
        <Text
          color="ink.strong"
          flex={1}
          fontFamily={active.meta.fontFamily}
          fontSize="ah-control"
          fontWeight={active.meta.fontWeight}
        >
          {active.meta.name}
        </Text>
      )}
      onChange={(id) => onChange(id as ExperienceVibe)}
      onPreview={(id) => onPreview(id as ExperienceVibe | null)}
    />
  );
};
